import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import {
  ADMIN_PAGE_GROUPS,
  adminPageForPath,
} from '@/components/admin/adminPages'
import { AdminMenuGroup } from './AdminMenuGroup'
import * as S from './AdminMenu.styled'

type AdminMenuPanelProps = {
  id: string
  onSelect: (path: string) => void
}

/**
 * The admin menu's panel, mounted only while the menu is open, so its links
 * leave the Tab order when it closes. Opening moves focus to its first link.
 */
export const AdminMenuPanel = forwardRef<HTMLElement, AdminMenuPanelProps>(
  ({ id, onSelect }, ref) => {
    const { pathname } = useLocation()
    const panelRef = useRef<HTMLElement>(null)
    useImperativeHandle(ref, () => panelRef.current as HTMLElement)
    const activePath = adminPageForPath(pathname)?.path ?? null

    useEffect(() => {
      panelRef.current?.querySelector('a')?.focus()
    }, [])

    return createPortal(
      <S.Panel
        ref={panelRef}
        id={id}
        aria-label="Admin menu"
        data-testid="admin-menu-panel"
      >
        <S.HomeTile
          to="/admin"
          data-testid="admin-menu-home"
          onClick={() => onSelect('/admin')}
        >
          <S.HomeMark aria-hidden="true">M</S.HomeMark>
          <S.HomeLabel>MDL Admin</S.HomeLabel>
        </S.HomeTile>
        <S.Groups data-testid="admin-menu-groups">
          {ADMIN_PAGE_GROUPS.map((group) => (
            <AdminMenuGroup
              key={group.label}
              group={group}
              activePath={activePath}
              onSelect={onSelect}
            />
          ))}
        </S.Groups>
        <S.BackLink to="/dashboard" onClick={() => onSelect('/dashboard')}>
          Back to dashboard
        </S.BackLink>
      </S.Panel>,
      document.body,
    )
  },
)

AdminMenuPanel.displayName = 'AdminMenuPanel'
